import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, ExternalLink, Search, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { API } from "../../App";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Section, EmptyState } from "./Section";
import { GROUP_META, fmtHours, fmtScore } from "./definitions";

const PAGE_SIZE = 25;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const HEAD = ["Ticket", "Company", "Contact", "Subject", "Group", "Topic", "Created", "Closed", "Time to close", "CSAT", "Escalation reason", ""];

const Row = ({ r }) => (
  <tr className="border-b border-slate-50 hover:bg-slate-50 text-slate-600" data-testid={`drilldown-row-${r.thread_id}`}>
    <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{r.ticket_id || `Chat ${r.thread_id}`}</td>
    <td className="py-2 pr-3 max-w-[140px] truncate">{r.company}</td>
    <td className="py-2 pr-3 max-w-[140px] truncate">{r.contact || "—"}</td>
    <td className="py-2 pr-3 max-w-[220px] truncate text-slate-800" title={r.subject}>{r.subject}</td>
    <td className="py-2 pr-3 whitespace-nowrap">
      <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ backgroundColor: GROUP_META[r.group].color }} />{GROUP_META[r.group].label}</span>
    </td>
    <td className="py-2 pr-3 whitespace-nowrap">{r.topic || "—"}</td>
    <td className="py-2 pr-3 whitespace-nowrap">{day(r.created_at)}</td>
    <td className="py-2 pr-3 whitespace-nowrap">{day(r.closed_at)}</td>
    <td className="py-2 pr-3 whitespace-nowrap">{fmtHours(r.time_to_close_h)}</td>
    <td className="py-2 pr-3">{fmtScore(r.csat)}</td>
    <td className="py-2 pr-3 whitespace-nowrap">{r.escalation_reason || "—"}</td>
    <td className="py-2">{r.link && <a href={r.link} target="_blank" rel="noreferrer" className="text-[#425B76]" data-testid={`drilldown-link-${r.thread_id}`}><ExternalLink className="w-4 h-4" /></a>}</td>
  </tr>
);

export const DrilldownTable = ({ params, version }) => {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { const t = setTimeout(() => { setQuery(search); setPage(1); }, 350); return () => clearTimeout(t); }, [search]);
  useEffect(() => { setPage(1); }, [params]);
  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/chatbot-metrics/tickets`, { params: { ...params, search: query || undefined, page, page_size: PAGE_SIZE }, withCredentials: true })
      .then((res) => setData(res.data))
      .catch(() => toast.error("Couldn't load the ticket list"))
      .finally(() => setLoading(false));
  }, [params, query, page, version]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/chatbot-metrics/tickets/export`, { params: { ...params, search: query || undefined }, withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `chatbot-metrics-${params.start}-to-${params.end}.csv` });
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("CSV export failed");
    }
  };

  const pages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  return (
    <Section title="All tickets" testId="drilldown-section" subtitle={data ? `${data.total.toLocaleString()} conversations match the filters` : "Loading…"}
      action={<Button variant="outline" size="sm" className="gap-2 border-slate-200" onClick={exportCsv} data-testid="export-csv-button"><Download className="w-4 h-4" />Export CSV</Button>}>
      <div className="relative mb-3 max-w-sm">
        <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search subject, company, contact, ID…" className="pl-9 h-9" data-testid="drilldown-search" />
      </div>
      {loading && !data ? <div className="h-32 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div> : null}
      {data && data.rows.length === 0 && <EmptyState message="No tickets match." testId="drilldown-empty" />}
      {data && data.rows.length > 0 && (
        <div className={`overflow-x-auto ${loading ? "opacity-60" : ""}`}>
          <table className="w-full text-sm" data-testid="drilldown-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">{HEAD.map((h) => <th key={h} className="py-2 pr-3 font-medium whitespace-nowrap">{h}</th>)}</tr></thead>
            <tbody>{data.rows.map((r) => <Row key={r.thread_id} r={r} />)}</tbody>
          </table>
        </div>
      )}
      {data && data.total > PAGE_SIZE && (
        <div className="flex items-center justify-end gap-2 mt-3 text-sm text-slate-500">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="drilldown-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <span data-testid="drilldown-page">Page {page} of {pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(page + 1)} data-testid="drilldown-next"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      )}
    </Section>
  );
};
